/**
 * Ring sizer UI.
 *
 * Wires the markup rendered by blocks/ring-sizer.liquid to the measurement logic
 * in ring-sizer-engine.js. Everything Shopify-specific stops at the block; this
 * element only reads data attributes.
 */
if (!customElements.get('ring-sizer')) {
  const SYSTEM_NAMES = { us: 'US', eu: 'EU', es: 'ES' };

  class RingSizer extends HTMLElement {
    connectedCallback() {
      this.engine = window.RingSizerEngine;
      if (!this.engine) return;

      this.system = this.dataset.system || 'us';
      this.minTenths = Math.round(Number(this.dataset.minDiameter || 14) * 10);
      this.maxTenths = Math.round(Number(this.dataset.maxDiameter || 22.5) * 10);

      this.steps = {
        desktop: this.querySelector('[data-step="desktop"]'),
        calibrate: this.querySelector('[data-step="calibrate"]'),
        measure: this.querySelector('[data-step="measure"]')
      };
      this.referenceSelect = this.querySelector('[data-reference-select]');
      this.referenceShape = this.querySelector('[data-reference-shape]');
      this.referenceCaption = this.querySelector('[data-reference-caption]');
      this.calibrationSlider = this.querySelector('[data-calibration-slider]');
      this.calibrationStatus = this.querySelector('[data-calibration-status]');
      this.ring = this.querySelector('[data-ring]');
      this.ringSlider = this.querySelector('[data-ring-slider]');
      this.equivalentsEl = this.querySelector('[data-equivalents]');
      this.confirmButton = this.querySelector('[data-confirm-size]');
      this.confirmedEl = this.querySelector('[data-confirmed]');

      this.numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      // Every coin stays selectable, but only the shopper's own currency is
      // considered when picking a default: a US quarter and a Canadian one differ
      // by enough to shift the result a whole ring size.
      this.referenceIds = Array.from(this.referenceSelect.options).map((option) => option.value);
      this.preferredIds = (this.dataset.preferred || '').split(',').filter(Boolean);
      if (!this.preferredIds.length) this.preferredIds = this.referenceIds;

      // A calibration the shopper performed themselves wins over a device we
      // recognise by its native resolution — unless the two disagree outright.
      // On a recognised panel the density is a hardware fact, so a stored value
      // 10% away is not a fine-tune, it's a careless drag of the calibration
      // slider: saved once, silently poisoning every measurement since, with
      // nothing on screen saying a stored value is even in play. Drop it and
      // fall back to the profile; deliberate corrections land well inside the
      // window and survive.
      const stored = this.engine.loadCalibration();
      const detected = this.engine.recognisedPxPerMm();
      const storedDisagrees = stored && detected
        && Math.abs(stored - detected) / detected > 0.1;
      if (storedDisagrees) this.engine.clearCalibration();

      if (stored && !storedDisagrees) {
        this.pxPerMm = stored;
        this.calibrationSource = 'shopper';
      } else if (detected) {
        this.pxPerMm = detected;
        this.calibrationSource = 'device';
      } else {
        // Never park a shopper at the coin step: measuring starts immediately
        // on the engine's best guess, and the measure step shows an
        // "approximate" note with calibration one tap away. The guess is a
        // compromise for known-ambiguous panels (~3% off at worst) or the
        // class-of-device estimate elsewhere.
        this.pxPerMm = this.engine.fallbackPxPerMm();
        this.calibrationSource = 'estimate';
      }

      this.reference = this.engine.defaultReference(this.preferredIds, window.innerWidth - 48);

      this.ringSlider.min = this.minTenths;
      this.ringSlider.max = this.maxTenths;
      this.ringSlider.value = this.clampTenths(Number(this.ringSlider.value) || 181);

      this.bindEvents();
      this.showStep(this.firstStep());

      // Inactive tab panels carry the hidden attribute, so this block usually
      // mounts with no box at all and no idea how wide it will be. Watch for the
      // size arriving, and watch for the block becoming visible, because a missed
      // notification would leave a shopper trying to match a card outline against
      // a screen that cannot fit one.
      this.resizes = new ResizeObserver(() => this.syncBounds());
      this.resizes.observe(this);

      this.reveals = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.syncBounds();
      });
      this.reveals.observe(this);

      // Pinch zoom scales rendered CSS pixels without a single layout event, so
      // neither observer above notices. The circle is a measuring instrument:
      // when the visual viewport's scale moves, redraw so it holds its physical
      // size instead of growing with the page.
      if (window.visualViewport) {
        this.onViewportScale = () => {
          this.drawReference();
          if (this.pxPerMm) this.drawRing();
        };
        window.visualViewport.addEventListener('resize', this.onViewportScale);
      }
    }

    disconnectedCallback() {
      if (this.resizes) this.resizes.disconnect();
      if (this.reveals) this.reveals.disconnect();
      if (this.onViewportScale) {
        window.visualViewport.removeEventListener('resize', this.onViewportScale);
      }
    }

    /**
     * How much bigger a rendered CSS pixel currently is than its layout size.
     * Pinch zoom does this, and so does the page zoom iOS applies by itself
     * when a sub-16px input takes focus — and keeps after the picker closes.
     * Dividing drawn widths by this holds the shapes at true physical size
     * whatever the page is doing; the calibration maths is unaffected, because
     * the same division cancels out of the pixels-per-millimetre quotient.
     */
    zoomScale() {
      return (window.visualViewport && window.visualViewport.scale) || 1;
    }

    /** Recomputes only when the box we last measured against has actually changed. */
    syncBounds() {
      if (this.clientWidth > 0 && this.clientWidth !== this.measuredWidth) this.refreshBounds();
    }

    /**
     * Desktop visitors are steered to their phone. Measuring means laying a ring
     * flat on the glass, which nobody can do against an upright monitor, and a
     * mismeasured ring is a return. They can override if they insist.
     */
    firstStep() {
      if (!this.engine.isHandheld() && !this.overrideDesktop) return 'desktop';
      return this.pxPerMm ? 'measure' : 'calibrate';
    }

    bindEvents() {
      this.calibrationSlider.addEventListener('input', () => {
        this.calibrationTouched = true;
        this.setCalibrationError('');
        this.drawReference();
      });
      this.ringSlider.addEventListener('input', () => {
        this.resetConfirmation();
        this.drawRing();
      });

      this.referenceSelect.addEventListener('change', () => {
        // A new object means the pixel width on screen means something else, so
        // the shopper's previous alignment is worthless rather than merely stale.
        this.reference = this.referenceSelect.value;
        this.calibrationTouched = false;
        this.setCalibrationError('');
        this.refreshBounds();
      });

      this.querySelector('[data-calibration-confirm]').addEventListener('click', () => {
        const { mm } = this.engine.REFERENCES[this.reference];
        const candidate = Number(this.calibrationSlider.value) / mm;
        if (!this.engine.isPlausible(candidate)) {
          this.setCalibrationError(this.dataset.calibrationError || '');
          return;
        }

        this.setCalibrationError('');
        this.pxPerMm = candidate;
        this.calibrationSource = 'shopper';
        this.engine.saveCalibration(candidate);
        this.showStep('measure', { focus: true });
      });

      // Two routes in: the standing recalibrate link, and the link inside the
      // "approximate measurement" note.
      this.querySelectorAll('[data-recalibrate]').forEach((link) => {
        link.addEventListener('click', () => {
          this.engine.clearCalibration();
          this.pxPerMm = null;
          this.calibrationSource = null;
          this.showStep('calibrate', { focus: true });
        });
      });

      const override = this.querySelector('[data-desktop-continue]');
      if (override) {
        override.addEventListener('click', () => {
          this.overrideDesktop = true;
          this.showStep(this.firstStep(), { focus: true });
        });
      }

      if (this.confirmButton) {
        this.confirmButton.addEventListener('click', () => this.recordMeasurement());
      }
    }

    /**
     * The shopper says this is their size: record it, then get out of the way
     * and hand them back the product page.
     *
     * Only fires on the button, never on a drag — a slider being dragged records
     * curiosity, not ring sizes. Nothing personal leaves the page. The diameter
     * travels alongside the size because the millimetre is the measurement and
     * the size is only an interpretation of it: a later correction to the chart
     * can be replayed over the whole history, which is impossible if all that
     * was kept is the number 8.
     */
    recordMeasurement() {
      if (!this.lastResult) return;

      const measurement = {
        size: this.lastResult.label,
        system: this.system,
        diameterMm: Number(this.lastResult.diameterMm.toFixed(1)),
        circumferenceMm: Number(this.lastResult.circumferenceMm.toFixed(1)),
        // 'device' means the screen was inferred from the model rather than the
        // shopper matching a coin against it: a weaker measurement, and worth
        // knowing if some size ever looks unexpectedly popular.
        calibration: this.calibrationSource,
        productId: this.dataset.productId || null
      };

      this.publish(measurement);

      // In the pop-up, closing IS the confirmation: the shopper lands back on the
      // product with their size in hand. A standalone page has no dialog to close,
      // and close() would quietly do nothing — a button that appears not to work.
      // Confirm in place instead, and say where to go next.
      if (this.closest('modal-dialog')) {
        this.close();
      } else {
        this.showConfirmation(measurement);
      }
    }

    /**
     * Replaces the confirm button with the measured size and what to do next.
     * Takes focus, because hiding the focused button would otherwise drop focus to
     * <body> — and landing on the message is what reads it out.
     */
    showConfirmation(measurement) {
      const template = this.confirmedEl && this.confirmedEl.dataset.template;
      if (!template) return;

      // The attribute was escaped by Liquid; reading it back through dataset
      // hands us the original markup.
      this.confirmedEl.innerHTML = template.split('__RING_SIZE__').join(measurement.size);
      this.confirmedEl.hidden = false;
      if (this.confirmButton) this.confirmButton.hidden = true;
      this.confirmedEl.focus();
    }

    /**
     * A confirmed size stops being true the moment the slider moves again, so the
     * message stands down and the button comes back.
     */
    resetConfirmation() {
      if (!this.confirmedEl || this.confirmedEl.hidden) return;
      this.confirmedEl.hidden = true;
      if (this.confirmButton) this.confirmButton.hidden = false;
    }

    /** Every sink is optional and none may keep the shopper waiting. */
    publish(measurement) {
      try {
        window.dataLayer = window.dataLayer || [];

        // GTM merges every push into one shared model that outlives the push, so
        // a key as generic as `size` would quietly leak a ring size into any
        // other tag that reads one. Namespace them, and spell them exactly as
        // the GA4 parameters so nothing has to be translated in between.
        window.dataLayer.push({
          event: 'ring_size_measured',
          ring_size: measurement.size,
          ring_size_system: measurement.system,
          ring_diameter_mm: measurement.diameterMm,
          ring_circumference_mm: measurement.circumferenceMm,
          ring_calibration: measurement.calibration,
          ring_product_id: measurement.productId
        });
      } catch (error) {
        // Analytics must never take the sizer down with it.
      }

      const endpoint = this.dataset.endpoint;
      if (endpoint) {
        // keepalive lets the request outlive the dialog closing under it.
        fetch(endpoint, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(measurement)
        }).catch(() => {
          // A shopper who has found their size does not need to hear about our
          // logging server.
        });
      }

      this.dispatchEvent(new CustomEvent('ring-sizer:measured', {
        detail: measurement,
        bubbles: true
      }));
    }

    /** Returns the shopper to the product page, focus and all. */
    close() {
      const dialog = this.closest('modal-dialog');
      if (dialog && typeof dialog.close === 'function') dialog.close();
    }

    clampTenths(tenths) {
      return Math.min(Math.max(tenths, this.minTenths), this.maxTenths);
    }

    /** Usable width for the reference object, falling back to the viewport while hidden. */
    availableWidth() {
      const own = this.clientWidth;
      return (own > 0 ? own : window.innerWidth) - 32;
    }

    /**
     * Sizes the calibration slider to the reference object and rules out any
     * reference too large to fit across this screen. A card is 85.6 mm wide, so
     * on a phone it cannot physically be matched and a coin has to stand in.
     */
    refreshBounds() {
      const available = this.availableWidth();
      if (available <= 0) return;

      this.measuredWidth = this.clientWidth;
      const estimate = this.engine.estimatePxPerMm();

      Array.from(this.referenceSelect.options).forEach((option) => {
        const entry = this.engine.REFERENCES[option.value];
        option.disabled = Boolean(entry) && entry.mm * estimate > available;
      });

      this.referenceSelect.value = this.reference;
      if (this.referenceSelect.selectedOptions[0]?.disabled) {
        this.reference = this.engine.defaultReference(this.preferredIds, available);
        this.referenceSelect.value = this.reference;
      }

      const { mm } = this.engine.REFERENCES[this.reference];
      const slider = this.calibrationSlider;
      const aligned = Number(slider.value);
      slider.min = Math.round(mm * 2);
      slider.max = Math.round(Math.min(mm * 14, available));

      // A rotating phone or an opening tab must never wipe an alignment the
      // shopper has already made by hand; only the untouched guess is replaced.
      slider.value = this.calibrationTouched
        ? Math.min(Math.max(aligned, Number(slider.min)), Number(slider.max))
        : Math.round(Math.min(mm * (this.pxPerMm || estimate), Number(slider.max)));

      this.drawReference();
      if (this.pxPerMm) this.drawRing();
    }

    /**
     * Swaps the visible step.
     *
     * Focus stays put on the initial mount — nobody asked us to move it — but
     * follows every step the shopper opens themselves. The button they just
     * pressed is inside the step being hidden, so without this the focus falls to
     * <body>: silent for a screen reader, and outside the modal's focus trap,
     * whose focusin handler never fires and so lets the next Tab walk into the
     * page behind the open dialog.
     */
    showStep(name, { focus = false } = {}) {
      Object.entries(this.steps).forEach(([key, element]) => {
        if (element) element.hidden = key !== name;
      });

      if (name === 'calibrate') {
        this.calibrationTouched = false;
        this.setCalibrationError('');
      }

      // Set once on entering the step and never toggled again, so it cannot shift
      // the layout while the shopper is aligning their ring.
      const detectedNote = this.querySelector('[data-detected]');
      if (detectedNote) detectedNote.hidden = this.calibrationSource !== 'device';

      const showingEstimate = this.calibrationSource === 'estimate';

      const estimatedNote = this.querySelector('[data-estimated]');
      if (estimatedNote) estimatedNote.hidden = !showingEstimate;

      // The standing recalibrate link and the estimate note's link do the same
      // thing; only ever show one of them.
      const recalibrateNote = this.querySelector('[data-recalibrate-note]');
      if (recalibrateNote) recalibrateNote.hidden = showingEstimate;

      if (name === 'desktop') this.drawQrCode();

      this.refreshBounds();
      if (name === 'measure') this.drawRing();

      // After the step is drawn, so the shopper lands on a laid-out box.
      if (focus && this.steps[name]) this.steps[name].focus();
    }

    /**
     * Renders the QR code that hands a desktop visitor over to their phone, where
     * a ring can actually be laid flat on the glass.
     *
     * QRCode comes from Shopify's own CDN (vendor/qrcode.js), so it is absent
     * whenever that fails to load, and absent in the theme editor's first paint.
     * Nothing here is load-bearing: the link beside the canvas already carries the
     * URL, and it is what assistive tech reads either way.
     */
    drawQrCode() {
      const holder = this.querySelector('[data-qr]');
      if (!holder || holder.dataset.qrDrawn) return;

      const url = holder.dataset.qrUrl;
      if (!url || typeof window.QRCode !== 'function') return;

      holder.dataset.qrDrawn = 'true';
      try {
        // Fixed black on white, never the theme's palette: a QR code is read by a
        // camera thresholding light against dark, and a low-contrast pair (or an
        // inverted one, on a dark theme) simply will not scan.
        new window.QRCode(holder, {
          text: url,
          width: 180,
          height: 180,
          colorDark: '#000000',
          colorLight: '#ffffff'
        });
        // The library stamps its own alt text on the <img> (a gift-card string on this
        // store). The holder is aria-hidden and the link beside it is the real
        // alternative, so the image is decorative: empty alt, nothing misleading left behind.
        holder.querySelectorAll('img').forEach((img) => img.setAttribute('alt', ''));
      } catch (error) {
        // Leave the link standing on its own.
        delete holder.dataset.qrDrawn;
      }
    }

    /**
     * "It matches" refuses an implausible calibration by returning, which looks
     * from the outside exactly like a dead button. Say so out loud.
     */
    setCalibrationError(message) {
      // Kept in the accessibility tree even when empty: a live region that is
      // added or unhidden at the moment of the error announces unreliably.
      if (this.calibrationStatus) this.calibrationStatus.textContent = message;
    }

    /** Draws the reference object at whatever size the shopper has dialled in. */
    drawReference() {
      const entry = this.engine.REFERENCES[this.reference];
      const widthPx = Number(this.calibrationSlider.value);
      const drawnPx = widthPx / this.zoomScale();

      this.referenceShape.style.width = `${drawnPx}px`;
      this.referenceShape.style.height = `${entry.shape === 'circle' ? drawnPx : drawnPx / entry.aspect}px`;
      this.referenceShape.style.borderRadius = entry.shape === 'circle' ? '50%' : '';

      this.referenceCaption.textContent = `${this.numberFormat.format(entry.mm)} mm`;

      // aria-valuetext replaces aria-valuenow outright, so whatever it says has to
      // be the part that moves. Naming only the object announced the same
      // "85.60 mm" on every arrow key and told the shopper nothing at all; the
      // raw value is a pixel count, which tells them no more. The percentage
      // through the range is the one honest number here, and the object's true
      // size rides along as the target being aimed at.
      const min = Number(this.calibrationSlider.min);
      const max = Number(this.calibrationSlider.max);
      const progress = max > min ? Math.round(((widthPx - min) / (max - min)) * 100) : 0;

      this.calibrationSlider.setAttribute(
        'aria-valuetext',
        `${progress}% — ${this.numberFormat.format(entry.mm)} mm`
      );
    }

    /** Draws the ring at true physical size and reports the matching size. */
    drawRing() {
      if (!this.pxPerMm) return;

      const diameterMm = Number(this.ringSlider.value) / 10;
      const result = this.engine.describe(this.system, diameterMm);
      const diameterText = `${this.numberFormat.format(diameterMm)} mm`;

      const drawnPx = (diameterMm * this.pxPerMm) / this.zoomScale();
      this.ring.style.width = `${drawnPx}px`;
      this.ring.style.height = `${drawnPx}px`;

      this.querySelector('[data-size]').textContent = result.label;
      this.querySelector('[data-diameter]').textContent = diameterText;
      this.querySelector('[data-circumference]').textContent =
        `${this.numberFormat.format(result.circumferenceMm)} mm`;

      if (this.equivalentsEl) {
        this.equivalentsEl.textContent = result.equivalents
          .map((entry) => `${SYSTEM_NAMES[entry.system]} ${entry.label}`)
          .join(' · ');
      }

      this.lastResult = result;

      this.ringSlider.setAttribute('aria-valuetext', `${result.label} — ${diameterText}`);
    }
  }

  customElements.define('ring-sizer', RingSizer);
}
